// DI-2 — 30+ 누적 도메인 fix 본질 흡수 mapping (D7=A 자동 추출, 2026-05-19).
//
// 점진 도입 시기 (2026-05-14 ~ 2026-05-18)에 patch level로 추가된 fix들이
// 100% rewrite 새 layer 구조(src/Agent/**)로 어디 흡수됐는지 명시적 mapping.
//
// 데이터 source — commit message + 메모리 (project_*_complete.md 시리즈).
//
// 상태:
//   - 'absorbed': 본질이 새 layer에 100% 흡수
//   - 'partial':  본질 일부만 흡수 — 후속 phase에서 추가 흡수 또는 의사 결정 대기
//   - 'pending':  검증 필요 (코드 grep + behavior test 미수행)
//   - 'dropped':  rewrite 컨텍스트에서 본질 자체 폐기 (의도적)

export const MIGRATION_STATUS = Object.freeze({
  ABSORBED: 'absorbed',
  PARTIAL: 'partial',
  PENDING: 'pending',
  DROPPED: 'dropped',
});

export const PATCH_MIGRATION_MAP = Object.freeze([
  // B Layer — Behavior fix (2026-05-14)
  {
    patchId: 'B1',
    commitHash: '12ec48d5',
    essence: 'Hallucination 차단 (외래 raw 숫자 환각)',
    absorbedInto: ['TL-1 placeholderSubstitute', 'FL-2 finishPolicy NUMERIC_HALLUCINATION guard'],
    status: MIGRATION_STATUS.ABSORBED,
  },
  {
    patchId: 'B2',
    commitHash: 'b69269e3',
    essence: '응답 도출 실패 (empty-stop) 차단',
    absorbedInto: ['OL-3 completionMarker', 'CL-3 injectPlaceholderForEmpty'],
    status: MIGRATION_STATUS.ABSORBED,
  },
  {
    patchId: 'B3',
    commitHash: '0c3e45a9',
    essence: '인텐션 misfire 차단 (CATEGORY/WORKFLOW disambiguation)',
    absorbedInto: ['RL-1 intentGate', 'RL-4 INTENTION_APPENDS', 'RL-8 intentionLookup'],
    status: MIGRATION_STATUS.ABSORBED,
  },
  {
    patchId: 'B4-1',
    commitHash: '(2026-05-14)',
    essence: 'DISCOUNT modifier dropping 차단 (할인 회원 재등록률)',
    absorbedInto: ['RL-1 forceDimensions', 'RL-8 modifier preservation (95% 구조적)'],
    status: MIGRATION_STATUS.ABSORBED,
  },
  {
    patchId: 'B4-2',
    commitHash: '21903bbe',
    essence: 'entity guard LLM-as-judge (hardcode stop words 폐기)',
    absorbedInto: ['TL-3 entity_not_in_catalog detector', 'OL-6 outputReviewer (D6 결정 시)'],
    status: MIGRATION_STATUS.PARTIAL,
  },

  // Phase A — 영문화/간결화
  {
    patchId: 'PhaseA',
    commitHash: 'e0a9b16a,f73e3fb6',
    essence: '영문화 + 간결화 (token -30%, 한국어 -61%)',
    absorbedInto: ['RL-3 IDENTITY_OVERRIDE', 'RL-4 INTENTION_APPENDS (Korean: ... 격리)', 'feedback_prompt_emoji_korean_avoidance'],
    status: MIGRATION_STATUS.ABSORBED,
  },

  // P0 — Critical (2026-05-15)
  {
    patchId: 'P0-A1',
    commitHash: 'ba3588a4',
    essence: '출석률 환각 차단 + GRANULARITY 분기',
    absorbedInto: ['RL-1 GRANULARITY-aware detection (intent + key)', 'backend label swap (별도 인프라)'],
    status: MIGRATION_STATUS.ABSORBED,
  },
  {
    patchId: 'P0-A4',
    commitHash: '1099dc3d',
    essence: '다기간 비교 누락 차단 (multi-period misfire)',
    absorbedInto: ['TL-4 multiPeriodGranularity hook', 'TL-4 injectIntentionDefaults', 'RL-5 DATE_CALCULATION_BLOCK'],
    status: MIGRATION_STATUS.ABSORBED,
  },

  // P1 — D family (2026-05-15 ~ 2026-05-18)
  {
    patchId: 'P1-D2',
    commitHash: 'fa08b40e,6c3f597c',
    essence: 'backend totalCount 환각 (generic count keys 노출 차단)',
    absorbedInto: ['TL-1 placeholderSubstitute COUNT type', 'TL-3 ambiguous_count_key detector', 'TL-1 ambiguousCountKeys.js'],
    status: MIGRATION_STATUS.ABSORBED,
  },
  {
    patchId: 'P1-D2x',
    commitHash: 'e193ddae',
    essence: '빈 placeholder leak 다국어 차단 (substitute callback marker 보존)',
    absorbedInto: ['TL-1 placeholderSubstitute (callback fallback)'],
    status: MIGRATION_STATUS.ABSORBED,
  },
  {
    patchId: 'P1-D3',
    commitHash: 'f15b6046',
    essence: 'outro 거짓 cols 메타 차단 ({{TABLE_COLS}} placeholder)',
    absorbedInto: ['TL-1 placeholderSubstitute TABLE_COLS type', 'BUFFER_STATE_PROMPT [TABLE COLUMNS — STRICT]'],
    status: MIGRATION_STATUS.ABSORBED,
  },
  {
    patchId: 'P1-D3x',
    commitHash: '100a5f2f (reverted c0a61f5f)',
    essence: '[displayed columns] inject 시도 — 70% leak 실패로 revert',
    absorbedInto: ['(revert; lesson: prompt 강화 시 raw key 노출 역효과)'],
    status: MIGRATION_STATUS.DROPPED,
  },
  {
    patchId: 'P1-D3xx',
    commitHash: '2ed5426f',
    essence: 'finish raw key leak 출력 단 차단 (검사 대상 확장)',
    absorbedInto: ['TL-2 sanitizeSchema enforceStrict', 'TL-2 applyResponseRawKeyBlock', 'OL-5 critical-reexamine'],
    status: MIGRATION_STATUS.ABSORBED,
  },
  {
    patchId: 'P1-D4',
    commitHash: 'b43696cd',
    essence: 'SUMMARY scalar 단위 누락 차단 (key(unit)=value 형식)',
    absorbedInto: ['TL-1 placeholderSubstitute unit hint', 'Config/unitsByDimension', 'BUFFER_STATE_PROMPT [OUTPUT FORMAT]'],
    status: MIGRATION_STATUS.ABSORBED,
  },

  // P2 — Entity / Format
  {
    patchId: 'P2-E',
    commitHash: '21903bbe',
    essence: 'entity guard LLM-as-judge 교체 (== B4-2)',
    absorbedInto: ['TL-3 entity_not_in_catalog detector', 'OL-6 outputReviewer (D6)'],
    status: MIGRATION_STATUS.PARTIAL,
  },

  // 받침 (Korean grammar)
  {
    patchId: 'josa',
    commitHash: '6fe3dd40',
    essence: '받침 cosmetic LLM 위임 (다국어 안전)',
    absorbedInto: ['RL-3 BUFFER_STATE_PROMPT [LANGUAGE GRAMMAR]', '_autoFinishText 한국어 강제 폐기'],
    status: MIGRATION_STATUS.ABSORBED,
  },

  // Frontend rewrite anchors (2026-05-18)
  {
    patchId: 'B-frontend',
    commitHash: 'a53f9c5a',
    essence: 'DIMENSION_SCHEMA + INTENTION_DIMENSIONS 65 + DISPLAY_GUIDE (D1=B 결정)',
    absorbedInto: ['aiPrompts.js (source of truth)', 'RL-8 intentionLookup', 'DI-1 intentionAdapter'],
    status: MIGRATION_STATUS.ABSORBED,
  },
  {
    patchId: 'visualization-merge',
    commitHash: '81e26d36',
    essence: 'develop 머지 + 시각화 6 cherry-pick (show_ltv/attendance_chart/member_status/class_attendance/attendance_ranking/engagement_chart)',
    absorbedInto: ['nodeAgent.mjs stub', 'TOOL_DEFINITIONS 22 도구'],
    status: MIGRATION_STATUS.ABSORBED,
  },

  // Auxiliary patches (commit history 추출)
  {
    patchId: 'unit-concat',
    commitHash: '407bb656',
    essence: '단위 결합/% 누락 차단 (substitute raw number only + LLM 단위 부착)',
    absorbedInto: ['TL-1 placeholderSubstitute (raw number)', 'BUFFER_STATE_PROMPT unit hint'],
    status: MIGRATION_STATUS.ABSORBED,
  },
  {
    patchId: 'autoApplyTermDefinition',
    commitHash: '(pre-rewrite legacy)',
    essence: 'USER TERM mapping (사용자 정의 용어 → intention)',
    absorbedInto: ['RL-8 dimension lookup ?', 'TL-7 normalizeIntentionId (case/lookalike)'],
    status: MIGRATION_STATUS.PENDING,
  },
  {
    patchId: 'BUFFER_MISMATCH_GUARD',
    commitHash: '(pre-rewrite legacy)',
    essence: 'buffer ↔ 요청 불일치 검출',
    absorbedInto: ['FL-2 finishPolicy DISPLAY_REQUIRED', 'RL-1 forceDimensions'],
    status: MIGRATION_STATUS.PENDING,
  },
  {
    patchId: 'SQL_INTERSECT_auto_merge',
    commitHash: '(pre-rewrite legacy)',
    essence: '다기간 INTERSECT auto-merge',
    absorbedInto: ['TL-4 multiPeriodGranularity', 'backend QUERY_PROMPT_TEMPLATE (별도)'],
    status: MIGRATION_STATUS.PARTIAL,
  },
  {
    patchId: 'CHANGE_CLAIM_GUARD',
    commitHash: '(pre-rewrite legacy)',
    essence: '변화량 주장 (X% 증가) 검증',
    absorbedInto: ['FL-2 finishPolicy OUTRO_RAW_LEAK', 'TL-1 placeholderSubstitute CHANGE/CHANGE_RATE'],
    status: MIGRATION_STATUS.ABSORBED,
  },
  {
    patchId: 'EMPTY_DISPLAY_recovery',
    commitHash: '(pre-rewrite legacy)',
    essence: '빈 display 응답 recovery',
    absorbedInto: ['CL-3 injectPlaceholderForEmpty (확장)', 'OL-3 completionMarker'],
    status: MIGRATION_STATUS.ABSORBED,
  },
  {
    patchId: 'finish_strict_mode',
    commitHash: '(pre-rewrite legacy)',
    essence: 'finish 도구 strict mode + outro raw leak retry',
    absorbedInto: ['TL-2 enforceStrict', 'OL-5 buildRetryMessage'],
    status: MIGRATION_STATUS.ABSORBED,
  },
  {
    patchId: 'T0_path_inject',
    commitHash: '(pre-rewrite legacy)',
    essence: '첫 turn intent forcing',
    absorbedInto: ['RL-1 IntentGate registerIntentHints', 'FL-4 ContextCollector priority 90 oneShot'],
    status: MIGRATION_STATUS.ABSORBED,
  },
  {
    patchId: 'Token-Anchored-outro',
    commitHash: '(pre-rewrite legacy)',
    essence: 'placeholder 일반화 (raw 숫자 → {{TYPE:field}} marker)',
    absorbedInto: ['TL-1 placeholderSubstitute 11 type', '(이미 자산)'],
    status: MIGRATION_STATUS.ABSORBED,
  },
  {
    patchId: 'CATALOG_VALIDATION',
    commitHash: '21903bbe (P2-E 일부)',
    essence: '[CATALOG VALIDATION] LLM-as-judge guide inject',
    absorbedInto: ['TL-3 entity_not_in_catalog detector', 'OL-6 outputReviewer (D6 결정 시 일반화)'],
    status: MIGRATION_STATUS.PARTIAL,
  },
  {
    patchId: 'P3-C3_HOWTO_misroute',
    commitHash: '(메모리 reference)',
    essence: 'HOWTO intent gate (수수료/요금/가격 키워드 분기)',
    absorbedInto: ['RL-3 IDENTITY_OVERRIDE BASE_PROMPT', 'RL-1 HOWTO detector'],
    status: MIGRATION_STATUS.ABSORBED,
  },
  {
    patchId: 'member_to_stats_misroute',
    commitHash: '(메모리 reference)',
    essence: 'MEMBER list ↔ STATS misroute 차단 (category-aware)',
    absorbedInto: ['RL-4 INTENTION_APPENDS 9 categories', 'RL-1 forceDimensions'],
    status: MIGRATION_STATUS.ABSORBED,
  },
  {
    patchId: 'GAP_CLASSIFICATION',
    commitHash: '2fad3211',
    essence: 'CRITICAL/MINOR/AMBIGUOUS gap classification',
    absorbedInto: ['RL-6 GAP CLASSIFICATION block (CALL_API_UNIVERSAL_RULES)'],
    status: MIGRATION_STATUS.ABSORBED,
  },
  {
    patchId: 'avgAge_fabrication_block',
    commitHash: '2fad3211',
    essence: 'L31 avgAge fabrication 차단 (Universal NEVER 4건)',
    absorbedInto: ['RL-6 Universal NEVER 4건', 'RL-7 TIER 1 HARD BLOCKS'],
    status: MIGRATION_STATUS.ABSORBED,
  },
]);

// ────────────────────────────────────────────────
// Public API
// ────────────────────────────────────────────────

export function getMigrationByPatchId(patchId) {
  if (!patchId || typeof patchId !== 'string') return null;
  return PATCH_MIGRATION_MAP.find((e) => e.patchId === patchId) ?? null;
}

export function listByStatus(status) {
  if (!status) return [];
  return PATCH_MIGRATION_MAP.filter((e) => e.status === status);
}

export function listAbsorbedPatches() {
  return listByStatus(MIGRATION_STATUS.ABSORBED);
}

export function listPartialPatches() {
  return listByStatus(MIGRATION_STATUS.PARTIAL);
}

export function listPendingPatches() {
  return listByStatus(MIGRATION_STATUS.PENDING);
}

export function listDroppedPatches() {
  return listByStatus(MIGRATION_STATUS.DROPPED);
}

/**
 * @returns {{total, absorbed, partial, pending, dropped, absorbedPercentage}}
 */
export function getCoverageReport() {
  const total = PATCH_MIGRATION_MAP.length;
  const absorbed = listAbsorbedPatches().length;
  const partial = listPartialPatches().length;
  const pending = listPendingPatches().length;
  const dropped = listDroppedPatches().length;
  const absorbedPercentage = total > 0 ? Math.round((absorbed / total) * 100) : 0;
  return { total, absorbed, partial, pending, dropped, absorbedPercentage };
}

/**
 * 각 entry 무결성 검증 — patchId 중복 X + status enum 일치 + absorbedInto array.
 * @returns {{valid: boolean, errors: Array<{patchId, reason}>}}
 */
export function auditMigrationMap() {
  const errors = [];
  const seenIds = new Set();
  const validStatuses = new Set(Object.values(MIGRATION_STATUS));
  for (const e of PATCH_MIGRATION_MAP) {
    if (!e.patchId || typeof e.patchId !== 'string') {
      errors.push({ patchId: '(missing)', reason: 'patchId missing or not string' });
      continue;
    }
    if (seenIds.has(e.patchId)) {
      errors.push({ patchId: e.patchId, reason: 'duplicate patchId' });
    }
    seenIds.add(e.patchId);
    if (!validStatuses.has(e.status)) {
      errors.push({ patchId: e.patchId, reason: `invalid status: ${e.status}` });
    }
    if (!Array.isArray(e.absorbedInto) || e.absorbedInto.length === 0) {
      errors.push({ patchId: e.patchId, reason: 'absorbedInto missing or empty' });
    }
    if (!e.essence || typeof e.essence !== 'string') {
      errors.push({ patchId: e.patchId, reason: 'essence missing or not string' });
    }
  }
  return { valid: errors.length === 0, errors };
}
